import type { CustomerRevenueReportItem } from '@vy/types';
import { useMemo } from 'react';
import type { FilterField } from '../../../../components/new-design/vyTableFilter';
import { useLabels } from '../../../../hooks/requests/use-labels';
import {
  buildContractStatusFilterField,
  buildCountryFilterField,
  buildCurrencyFilterField,
  buildCustomerFilterField,
  buildLabelsFilterField,
  buildLabelsOptions,
  buildPlanFilterField,
  buildProductFilterField,
  buildStateFilterField,
  collectIdNameOptions,
  collectUniqueOptions,
  filterNonEmptySelectFields,
} from '../../reports-components/shared-filter-fields';
import { CUSTOMER_REVENUE_CURRENCY_FIELD_ID } from '../utils/customer-revenue-filter-field-ids';
import { getCustomerRevenueProductName } from '../customer-revenue-report-components/CustomerRevenueReportTable/customer-revenue-report-row-expander';

export function useCustomerRevenueFilterFields(
  reportItems: CustomerRevenueReportItem[],
): FilterField[] {
  const { data: labelsData } = useLabels();

  return useMemo(() => {
    const customers = collectIdNameOptions(
      reportItems.map((item) => ({ id: item.customer.id, name: item.customer.name })),
    );
    const countries = collectUniqueOptions(reportItems.map((item) => item.customer.country ?? null));
    const states = collectUniqueOptions(reportItems.map((item) => item.customer.state ?? null));
    const plans = collectUniqueOptions(reportItems.map((item) => item.planName ?? null));

    // Product options come from the products actually present in the report
    // (the same name shown in the Product column), keyed by productId — the
    // report payload doesn't carry catalogProducts/meterIds. Mirrors how the
    // Customer/Country/State filters are built and matched.
    const products = collectIdNameOptions(
      reportItems.map((item) => ({ id: item.productId, name: getCustomerRevenueProductName(item) })),
    );

    const presentLabelIds = new Set(
      reportItems.flatMap((item) => (item.customer.labels ?? []).map((label) => label.id)),
    );
    const labelOptions = buildLabelsOptions(labelsData?.labels, presentLabelIds);

    const allFields: FilterField[] = [
      buildCustomerFilterField(customers),
      buildCurrencyFilterField(CUSTOMER_REVENUE_CURRENCY_FIELD_ID),
      buildCountryFilterField(countries),
      buildStateFilterField(states),
      buildProductFilterField(products),
      buildPlanFilterField(plans),
      buildContractStatusFilterField(),
      buildLabelsFilterField(labelOptions),
    ];

    return filterNonEmptySelectFields(allFields);
  }, [reportItems, labelsData]);
}
